import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { LucideAngularModule, Calendar, X, CheckCircle } from 'lucide-angular';

/**
 * TVG-134: Capture dialog shown when a card is dragged into the Viewing Booked
 * column. The column is defined by an event having a viewing date set, so the
 * dialog asks for the date (time optional). Booking persists the date and the
 * card auto-promotes into Viewing Booked.
 *
 * The always-available override — "Skip the viewing" — completes the gesture
 * without booking anything: no state change, so the card returns to Active
 * Opportunity and the sales manager can drag it straight on to Pencil/Confirmed.
 * Per Mark's TVG-134 scope decision the board never forces a card through a
 * viewing.
 */
export interface ViewingDatePayload {
  show_round_date: string;
  show_round_time: string | null;
}

@Component({
  selector: 'app-viewing-date-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, LucideAngularModule],
  template: `
    @if (isOpen) {
      <div class="fixed inset-0 z-[1000] overflow-y-auto" data-testid="viewing-date-modal">
        <div class="flex min-h-screen items-center justify-center p-4">
          <div class="fixed inset-0 bg-black/50 transition-opacity" (click)="onSkip()"></div>

          <div class="relative z-10 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
            <div class="flex items-start gap-4 p-6">
              <div class="flex h-11 w-11 items-center justify-center rounded-full bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300">
                <lucide-icon [name]="CalendarIcon" [size]="22"></lucide-icon>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                  Book a viewing
                </h3>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  Set the viewing date to move this card into Viewing Booked. No date? Skip the
                  viewing and the card stays an Active Opportunity.
                </p>
              </div>
              <button
                class="text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                (click)="onSkip()"
                aria-label="Close book viewing modal">
                <lucide-icon [name]="XIcon" [size]="18"></lucide-icon>
              </button>
            </div>

            <form [formGroup]="form" class="space-y-3 px-6 pb-2">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="viewing-date">
                  Viewing date
                </label>
                <input
                  id="viewing-date"
                  type="date"
                  formControlName="date"
                  data-testid="viewing-date-input"
                  class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="viewing-time">
                  Time (optional)
                </label>
                <input
                  id="viewing-time"
                  type="time"
                  formControlName="time"
                  data-testid="viewing-time-input"
                  class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
              </div>
            </form>

            <div class="mt-4 flex items-center justify-end gap-3 bg-gray-50 px-6 py-4 dark:bg-gray-800/60">
              <app-button
                [label]="'Skip the viewing'"
                [style]="'secondary'"
                [icon]="XIcon"
                [disabled]="submitting"
                data-testid="viewing-date-skip"
                (click)="onSkip()">
              </app-button>
              <app-button
                [label]="submitting ? 'Booking…' : 'Book viewing'"
                [style]="'primary'"
                [icon]="CheckIcon"
                [disabled]="submitting || form.invalid"
                data-testid="viewing-date-submit"
                (click)="onConfirm()">
              </app-button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class ViewingDateModalComponent {
  @Input() isOpen = false;
  @Input() submitting = false;

  protected readonly form = new FormGroup({
    date: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    time: new FormControl<string>('', { nonNullable: true }),
  });

  /** Emits the captured date/time so the board can book the viewing. */
  @Output() confirmed = new EventEmitter<ViewingDatePayload>();
  /** Override / dismiss — the move is abandoned (no state change). */
  @Output() skipped = new EventEmitter<void>();

  protected readonly CalendarIcon = Calendar;
  protected readonly XIcon = X;
  protected readonly CheckIcon = CheckCircle;

  onConfirm(): void {
    if (this.submitting || this.form.invalid) {
      return;
    }
    this.confirmed.emit({
      show_round_date: this.form.controls.date.value,
      show_round_time: this.form.controls.time.value || null,
    });
  }

  onSkip(): void {
    this.skipped.emit();
  }

  reset(): void {
    this.form.reset({ date: '', time: '' });
  }
}
